/**
 * Grace Period Modal Hook
 *
 * Manages state and logic for the grace period modal.
 * This modal appears when a user with 2+ projects logs in during the grace period,
 * before their FEATURE_LIMIT_EXCEEDED action is triggered.
 *
 * @module @ideoz/core/hooks/facades/shared/useGracePeriodModal
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAppSelector } from '../../../store/hooks';
import { authService } from '../../../api/services/auth.service';
import type { NavigationFacade, ShowToastFn } from './types';
import type { PlanDetails } from '../../../types/subscription.types';
import type { User } from '../../../types/user.types';

/**
 * State returned by useGracePeriodModal hook
 */
export interface GracePeriodModalState {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Whether an action is processing
   */
  isProcessing: boolean;

  /**
   * Grace period end date (ISO 8601 format)
   */
  gracePeriodEndTime?: string;

  /**
   * Handler for closing the modal (X button or "Got it")
   */
  onClose: () => void;

  /**
   * Handler for "View Plans" action
   */
  onViewPlans: () => void;
}

/**
 * Parameters for useGracePeriodModal hook
 */
export interface UseGracePeriodModalParams {
  /**
   * Navigation facade for redirecting
   */
  navigation: NavigationFacade;

  /**
   * Plan details from user profile (contains subscriptionInfo and features)
   */
  planDetails?: PlanDetails | null;

  /**
   * User data (contains metadata.gracePeriodNoticeShown)
   */
  user?: User | null;

  /**
   * Toast notification function
   */
  showToast?: ShowToastFn;

  /**
   * Callback to handle view plans flow - used in plugin to open web billing
   * If not provided, web will navigate to billing page
   */
  onViewPlans?: () => void;
}

/**
 * Hook to manage grace period modal state and actions
 *
 * The modal is triggered when:
 * 1. User has metadata.gracePeriodNoticeShown === false (or not set)
 * 2. User has features with Projects balance > 1
 * 3. User does NOT have actionType === 'FEATURE_LIMIT_EXCEEDED' for projects
 *
 * @param params - Configuration parameters
 * @returns Grace period modal state and handlers
 *
 * @example
 * ```tsx
 * const gracePeriodModal = useGracePeriodModal({
 *   navigation,
 *   planDetails,
 *   user,
 *   showToast,
 *   onViewPlans: handleViewPlans,
 * });
 *
 * <GracePeriodModal
 *   isOpen={gracePeriodModal.isOpen}
 *   onClose={gracePeriodModal.onClose}
 *   onViewPlans={gracePeriodModal.onViewPlans}
 *   gracePeriodEndTime={gracePeriodModal.gracePeriodEndTime}
 *   isProcessing={gracePeriodModal.isProcessing}
 * />
 * ```
 */
export function useGracePeriodModal({
  navigation,
  planDetails,
  user,
  showToast,
  onViewPlans,
}: UseGracePeriodModalParams): GracePeriodModalState {
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasAutoTriggered, setHasAutoTriggered] = useState(false);

  // Check if grace period notice has been shown
  const gracePeriodNoticeShown = useMemo(() => {
    return user?.metadata?.gracePeriodNoticeShown === true;
  }, [user]);

  // Get grace period end date
  const gracePeriodEndTime = useMemo(() => {
    return planDetails?.subscriptionInfo?.gracePeriodEndTime;
  }, [planDetails]);

  // Check if user has more than 1 project (balance > 1 in Projects feature)
  const hasMultipleProjects = useMemo(() => {
    const features = planDetails?.features || [];
    const projectsFeature = features.find(
      (feature) => feature.name?.toLowerCase() === 'projects'
    );
    // Balance represents the number of projects the user has
    const balance = projectsFeature?.balance ?? 0;
    return balance > 1;
  }, [planDetails]);

  // Check if projects feature is exceeded (blocking modal should show instead)
  const isProjectsExceeded = useMemo(() => {
    const subscriptionInfo = planDetails?.subscriptionInfo;
    if (subscriptionInfo?.actionType !== 'FEATURE_LIMIT_EXCEEDED') {
      return false;
    }
    const exceededFeatures = subscriptionInfo.exceededFeatures || [];
    return exceededFeatures.some(
      (feature) => feature.name?.toLowerCase() === 'projects'
    );
  }, [planDetails]);

  // Check if user is on Free plan
  const isFreePlan = useMemo(() => {
    return planDetails?.name?.toLowerCase() === 'free';
  }, [planDetails]);

  // Determine if grace period modal should show
  const shouldShowGracePeriodModal = useMemo(() => {
    // All conditions must be true:
    // 1. Grace period notice has NOT been shown
    // 2. User has more than 1 project
    // 3. User does NOT have FEATURE_LIMIT_EXCEEDED for projects (blocking modal takes priority)
    // 4. User is on Free plan
    return !gracePeriodNoticeShown && hasMultipleProjects && !isProjectsExceeded && isFreePlan;
  }, [gracePeriodNoticeShown, hasMultipleProjects, isProjectsExceeded, isFreePlan]);

  // Auto-trigger modal when conditions are met
  useEffect(() => {
    if (shouldShowGracePeriodModal && !isOpen && !hasAutoTriggered) {
      setIsOpen(true);
      setHasAutoTriggered(true);
    }
  }, [shouldShowGracePeriodModal, isOpen, hasAutoTriggered]);

  // Close modal and reset trigger when condition is no longer met
  useEffect(() => {
    if (!shouldShowGracePeriodModal && isOpen && hasAutoTriggered) {
      setIsOpen(false);
      setHasAutoTriggered(false);
    }
  }, [shouldShowGracePeriodModal, isOpen, hasAutoTriggered]);

  // Update grace period notice shown flag via API
  const updateGracePeriodNoticeShown = useCallback(async () => {
    try {
      await authService.updateGracePeriodNoticeShown();
    } catch (error) {
      console.error('Failed to update grace period notice shown flag:', error);
      // Don't show error to user - this is a background operation
    }
  }, []);

  // Handler for closing the modal (X button or "Got it")
  const handleClose = useCallback(async () => {
    setIsProcessing(true);
    
    // Update the flag via API (fire and forget - close modal optimistically)
    updateGracePeriodNoticeShown();
    
    setIsOpen(false);
    setIsProcessing(false);
  }, [updateGracePeriodNoticeShown]);

  // Handler for "View Plans" button
  const handleViewPlans = useCallback(async () => {
    setIsProcessing(true);
    
    // Update the flag via API (fire and forget)
    updateGracePeriodNoticeShown();
    
    // Close modal first
    setIsOpen(false);
    
    // Plugin: Use provided callback to open web billing
    if (onViewPlans) {
      onViewPlans();
      setIsProcessing(false);
      return;
    }

    // Web: Navigate to billing page
    if (navigation.toBilling) {
      navigation.toBilling();
    }
    
    setIsProcessing(false);
  }, [onViewPlans, navigation, updateGracePeriodNoticeShown]);

  return {
    isOpen,
    isProcessing,
    gracePeriodEndTime,
    onClose: handleClose,
    onViewPlans: handleViewPlans,
  };
}
